// Per-role session storage (v2.0 US-8.1).
//
// v1 kept one { token, user, role } triple in localStorage, so signing in as
// a parent evicted the coach session in the same browser (and vice versa).
// v2 stores one session per role and derives the ACTIVE role from the URL:
// /coach/* uses the coach session, /parent/* the parent session.

const KEYS = { coach: 'hh.session.coach', parent: 'hh.session.parent' };
const LEGACY = ['token', 'user', 'role'];

const safe = (fn, fallback) => {
  try { return fn(); } catch (e) { return fallback; }
};

export const ROLES = Object.keys(KEYS);

export const roleFromPath = (pathname = '') => {
  if (pathname.startsWith('/parent')) return 'parent';
  if (pathname.startsWith('/coach')) return 'coach';
  return null;
};

export const readSession = (role) =>
  safe(() => {
    const raw = localStorage.getItem(KEYS[role]);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.token && parsed.user ? parsed : null;
  }, null);

export const writeSession = (role, token, user) =>
  safe(() => localStorage.setItem(KEYS[role], JSON.stringify({ token, user })), undefined);

export const clearSession = (role) => safe(() => localStorage.removeItem(KEYS[role]), undefined);

export const updateSessionUser = (role, patch) => {
  const current = readSession(role);
  if (!current) return null;
  const user = { ...current.user, ...patch };
  writeSession(role, current.token, user);
  return user;
};

// One-time upgrade of a v1 session into its role-namespaced slot.
export const migrateLegacySession = () =>
  safe(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const user = localStorage.getItem('user');
    if (token && user && KEYS[role] && !readSession(role)) {
      localStorage.setItem(KEYS[role], JSON.stringify({ token, user: JSON.parse(user) }));
    }
    LEGACY.forEach((k) => localStorage.removeItem(k));
  }, undefined);

// The role whose session should serve a request from the current page.
// Inside /coach or /parent the URL decides; elsewhere fall back to whichever
// session exists (coach first).
export const activeRole = (pathname = safe(() => window.location.pathname, '')) => {
  const fromPath = roleFromPath(pathname);
  if (fromPath) return readSession(fromPath) ? fromPath : null;
  if (readSession('coach')) return 'coach';
  if (readSession('parent')) return 'parent';
  return null;
};

export const loginPathFor = (role) => (role === 'parent' ? '/parent/login' : '/coach/login');

// X-5: where the installed app (manifest start_url "/app") and its shortcuts
// ("/app?to=schedule") land. A signed-in user goes straight to their portal
// (coach first when both sessions exist, like activeRole); anyone else gets
// the home page. `to` is limited to pages both portals have.
const APP_DESTINATIONS = ['dashboard', 'schedule', 'messages'];
export const appStartPath = (to) => {
  const dest = APP_DESTINATIONS.includes(to) ? to : 'dashboard';
  const role = readSession('coach') ? 'coach' : readSession('parent') ? 'parent' : null;
  return role ? `/${role}/${dest}` : '/';
};
